import React, { useRef, useState } from "react";
import { Text, Flex, Button, toast } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { useDocumentDownloadUrlLazyQuery } from "#graphql/supergraphHooks";
import * as styles from "./styles_vanilla.css";
import { useSubtaskActionsContext } from "./SubtaskActionsContext";
import type { SubtaskItem } from "../types";

type UploadStatus =
  | { kind: "idle" }
  | { kind: "uploading" }
  | { kind: "staged"; documentId: number; fileName: string; fileSizeBytes: number }
  | { kind: "error"; message: string };

export type DocumentUploadProps = {
  item: SubtaskItem;
  disabled?: boolean;
};

const FileIconBadge: React.FC = () => (
  <Flex alignItems="center" justifyContent="center" className={styles.fileIconBadge}>
    <Icon type="file-alt" color={Colors.Neutral600} size="medium" variant="solid" />
  </Flex>
);

export const DocumentUpload: React.FC<DocumentUploadProps> = ({ item, disabled = false }) => {
  const {
    stageDocument,
    deleteUploadedDocument,
    detachDocument,
    updateSubtaskValue,
    taskDocuments,
    stagedTaskDocuments,
  } = useSubtaskActionsContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<UploadStatus>({ kind: "idle" });
  const [existingDeleted, setExistingDeleted] = useState(false);
  const [fetchDownloadUrl, { loading: downloadLoading }] = useDocumentDownloadUrlLazyQuery();

  const purpose = (item.config?.purpose as string) || "OPTIONAL";
  const acceptedTypes = (item.config?.acceptedTypes as string) ?? null;
  const existingFileName = (() => {
    if (typeof item.value !== "string" || !item.value) return null;
    try {
      const parsed: unknown = JSON.parse(item.value);
      if (Array.isArray(parsed) && typeof parsed[0] === "string") return parsed[0];
    } catch {
      // not JSON — plain filename string
    }
    return item.value;
  })();
  const existingDocumentId =
    existingFileName != null
      ? (taskDocuments.find((d) => d.fileName === existingFileName)?.documentId ?? null)
      : null;
  const stagedDocumentId =
    existingFileName != null
      ? (stagedTaskDocuments.find((d) => d.fileName === existingFileName)?.documentId ?? null)
      : null;

  const handleDownload = async (documentId: number) => {
    try {
      const { data } = await fetchDownloadUrl({ variables: { id: documentId } });
      const url = data?.documentDownloadUrl;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        toast.error("Download link is unavailable. Please try again later.");
      }
    } catch {
      toast.error("Failed to download document. Please try again.");
    }
  };

  const triggerPicker = () => {
    if (disabled || status.kind === "uploading") return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (status.kind === "staged") {
      await deleteUploadedDocument(status.documentId);
    }

    setStatus({ kind: "uploading" });
    try {
      const documentId = await stageDocument(file, purpose);
      setStatus({ kind: "staged", documentId, fileName: file.name, fileSizeBytes: file.size });
      await updateSubtaskValue(item.id, file.name, true);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload failed.";
      setStatus({ kind: "error", message });
      toast.error(message);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDeleteStaged = async () => {
    if (status.kind !== "staged") return;
    await deleteUploadedDocument(status.documentId);
    await updateSubtaskValue(item.id, "", false);
    setStatus({ kind: "idle" });
  };

  const handleDeleteExisting = () => {
    setExistingDeleted(true);
    // If it's a staged doc (uploaded but not yet submitted), delete it from S3 + staged list.
    // If it's an attached doc, detach it from the task server-side.
    const ops: Promise<void>[] = [];
    if (stagedDocumentId != null) {
      ops.push(deleteUploadedDocument(stagedDocumentId));
    } else if (existingDocumentId != null) {
      ops.push(detachDocument(existingDocumentId));
    }
    void Promise.all(ops).then(() => updateSubtaskValue(item.id, "", false));
  };

  return (
    <Flex flexDirection="column" gap={Sizes.XS} data-testid="subtask-document-upload">
      {item.label && (
        <span className={styles.uploadLabel}>
          {item.label}
          {item.isRequired && (
            <span
              data-testid="required-asterisk"
              style={{ color: Colors.Danger600, marginLeft: 2 }}
            >
              *
            </span>
          )}
        </span>
      )}
      {item.helpText && (
        <Text variant="secondary-sm" color={Colors.Neutral500} className={styles.helpText}>
          {item.helpText}
        </Text>
      )}

      <input
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={(e) => {
          void handleFileChange(e);
        }}
        disabled={disabled}
        data-testid="subtask-document-upload-input"
      />

      {!disabled && (
        <Flex
          alignItems="center"
          gap={Sizes.MD}
          className={styles.uploadDropZone}
          data-testid="subtask-document-upload-zone"
        >
          <Button
            onClick={triggerPicker}
            disabled={status.kind === "uploading"}
            className={styles.uploadChooseButton}
            data-testid="subtask-document-upload-button"
            variant="neutral"
          >
            {status.kind === "uploading" ? "Uploading…" : "Upload File"}
          </Button>
          {acceptedTypes && (
            <Text variant="secondary-sm" color={Colors.Neutral500}>
              {acceptedTypes}
            </Text>
          )}
        </Flex>
      )}

      {status.kind === "staged" && (
        <Flex
          alignItems="center"
          gap={Sizes.MD}
          className={styles.uploadCard}
          data-testid="subtask-document-upload-card"
        >
          <FileIconBadge />
          <Flex flexDirection="column" gap="2px" style={{ flex: 1, minWidth: 0 }}>
            <span
              className={styles.uploadFileName}
              data-testid="subtask-document-upload-filename"
            >
              {status.fileName}
            </span>
          </Flex>
          {!disabled && (
            <Button
              onClick={() => {
                void handleDeleteStaged();
              }}
              className={styles.uploadIconButton}
              aria-label="Remove file"
              data-testid="subtask-document-upload-delete"
              icon="trash"
              variant="neutral"
            />
          )}
        </Flex>
      )}

      {status.kind === "idle" && existingFileName && !existingDeleted && (
        <Flex
          alignItems="center"
          gap={Sizes.MD}
          className={styles.uploadCard}
          data-testid="subtask-document-existing-card"
        >
          <FileIconBadge />
          <Flex flexDirection="column" gap="2px" style={{ flex: 1, minWidth: 0 }}>
            <span
              className={styles.uploadFileName}
              data-testid="subtask-document-upload-filename"
            >
              {existingFileName}
            </span>
          </Flex>
          {existingDocumentId != null && (
            <Button
              onClick={() => {
                void handleDownload(existingDocumentId);
              }}
              className={styles.uploadIconButton}
              aria-label={`Download ${existingFileName}`}
              data-testid="subtask-document-download"
              disabled={downloadLoading}
              icon="download"
              variant="neutral"
            />
          )}
          {!disabled && (
            <Button
              onClick={handleDeleteExisting}
              className={styles.uploadIconButton}
              aria-label="Remove file"
              data-testid="subtask-document-existing-delete"
              icon="trash"
              variant="neutral"
            />
          )}
        </Flex>
      )}

      {status.kind === "error" && (
        <Text variant="secondary-sm" color={Colors.Danger600}>
          {status.message}
        </Text>
      )}
    </Flex>
  );
};

export default DocumentUpload;
